import { getSxByRank } from '@app/src/pages/QuestionEditor/AnswerClassification/ClassificationRow'
import type { AssessmentRule, QuestionAnswerClassificationRule, ResponseItem } from '@app/src/types/resourceExplorer'
import { AnswerRuleType } from '@app/src/types/resourceExplorer'
import { formatNumberAsStringWithPrependedPlus } from '@app/src/utils/helpers'
import { Chip, Stack, Typography } from '@mui/material'
import React from 'react'
import type { IntlFormatters } from 'react-intl'
import { useIntl } from 'react-intl'
import { getClassificationScores } from './assessmentAnswersLogic'

interface ClassificationScoresProps {
  item: ResponseItem
  rules: AssessmentRule[]
}

const NUMBER_RULE_TYPES = [
  AnswerRuleType.NumberEqualTo,
  AnswerRuleType.NumberGreaterThan,
  AnswerRuleType.NumberLowerThan,
]

const getRuleLabel = (
  rule: QuestionAnswerClassificationRule | undefined,
  formatMessage: IntlFormatters['formatMessage'],
): string => {
  if (!rule) return ''
  if (rule.ruleType === AnswerRuleType.SelectedValue) return String(rule.value ?? '')
  if (rule.ruleType === AnswerRuleType.AnswerStatus) {
    return formatMessage({ id: `templateBuilder.answerClassifications.classificationsValues.${rule.value}` })
  }
  if (NUMBER_RULE_TYPES.includes(rule.ruleType)) {
    return formatMessage(
      { id: 'templateBuilder.answerClassifications.numberValue' },
      { ruleType: rule.ruleType, value: String(rule.value ?? '') },
    )
  }
  return ''
}

const ClassificationScores: React.FC<ClassificationScoresProps> = ({ item, rules }) => {
  const { formatMessage } = useIntl()
  const scores = getClassificationScores(item, rules)

  if (scores.length === 0) return null

  return (
    <Stack>
      {scores.map(({ result, points }) => {
        const rule = result.questionAnswerClassificationRule
        const rank = rule?.rank ?? result.rank
        return (
          <Stack
            key={result.id}
            direction="row"
            alignItems="center"
            spacing={2}
            py={1}
            px={2}
            borderTop="1px solid"
            borderColor="divider"
          >
            <Typography variant="body2" color="text.secondary" flex={1} minWidth={0}>
              {getRuleLabel(rule, formatMessage)}
            </Typography>
            <Chip
              size="small"
              label={formatMessage({ id: `templateBuilder.answerClassifications.classificationsRanks.${rank}` })}
              sx={({ palette }) => ({ ...getSxByRank(palette, rank), minWidth: 80 })}
            />
            <Typography variant="body2" width={110} textAlign="right" flexShrink={0}>
              {formatMessage(
                { id: 'baselineAssessment.providerPage.points' },
                { points: formatNumberAsStringWithPrependedPlus(points) },
              )}
            </Typography>
          </Stack>
        )
      })}
    </Stack>
  )
}

export default ClassificationScores
